import { faCompress, faExpand } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import dayjs from "dayjs";
import React from "react";
import { globalAppConfig } from "../config";
import Helper from "../Helper";
import { CommandCenterView } from "../models/CommandCenterView";
import Clock from "./clock";
import { CommandProjectInfo } from "./CommandTypes";
import { JobsiteCommandContext } from "./JobsiteCommandProvider";
import JobsiteHours from "./jobsiteHoursUI";

interface CommandCenterHeaderProps {
    projectInfo: CommandProjectInfo;
    commandCenterView?: CommandCenterView;
}

const CommandCenterHeader: React.FC<CommandCenterHeaderProps> = ({ projectInfo, commandCenterView }) => {
    const commandContext = React.useContext(JobsiteCommandContext);

    const [now, _] = React.useState(() => new Date());

    const toggleFullScreen = () => {
        try {
            if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen();
            } else if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        } catch (err) {
            notification.error({
                message: "Fullscreen mode not supported on this device.",
                description: String(err),
                placement: "topRight",
                duration: globalAppConfig.NOTIFICATION_DURATION,
            });
        }
    };

    const isPortrait = commandContext?.isPortrait ?? false;

    return (
        <header
            className={Helper.classNames(
                "flex relative bg-neutral-900 border-b border-neutral-700 z-10",
                isPortrait ? "flex-col py-4 px-8" : "flex-row p-8"
            )}
        >
            <div style={{ zIndex: -1 }} className="absolute inset-0 flowing-gradient opacity-40"></div>
            <div className={Helper.classNames("relative flex w-full gap-8 items-center justify-between")}>
                <div className="flex flex-1 items-center gap-4 min-w-0">
                    <button className="cursor-pointer" type="button" onClick={toggleFullScreen}>
                        <FontAwesomeIcon
                            icon={document.fullscreenElement ? faCompress : faExpand}
                            className="text-plot-darkmode-white"
                        />
                    </button>

                    <div className="rounded-lg flex items-center justify-center shadow-lg gc-logo-mask">
                        <img src={projectInfo?.avatar_url} alt="Team Logo" className="w-32 h-32 object-cover" />
                    </div>
                    <div className="flex flex-1 flex-col w-full">
                        <h1
                            className={Helper.classNames(
                                "font-bold text-white uppercase line-clamp-2",
                                isPortrait ? "text-3xl" : "text-5xl"
                            )}
                        >
                            {projectInfo?.project_name}
                        </h1>
                        <div className="flex flex-row items-center gap-4 w-full">
                            <div className="flex flex-row gap-1 items-center">
                                <div className="w-3 h-3 rounded-full animate-pulse bg-plot-orange"></div>
                                <span className="font-medium text-sm text-plot-orange">LIVE</span>
                            </div>
                            <div className="text-sm text-gray-300">{commandCenterView?.name}</div>
                        </div>
                        <div
                            className={Helper.classNames(
                                "flex  w-full",
                                isPortrait ? "flex-col" : "flex-row items-center gap-2"
                            )}
                        >
                            <div className="text-sm text-gray-300">Last Updated:</div>
                            <div className="text-sm text-gray-300">
                                {dayjs(commandContext?.lastUpdated ?? now).format("MMMM D, YYYY h:mm A")}
                            </div>
                        </div>
                    </div>
                </div>
                {isPortrait ? (
                    <div className="flex flex-col">
                        <div className="flex flex-row items-end gap-8 ">
                            <div className="flex flex-col items-end gap-4">
                                <Clock timeZone={projectInfo?.timezone} />
                            </div>
                            <img
                                src="/img/png/logo/plot_jobsite_command.png"
                                alt="PLOT Logo"
                                className="opacity-90 h-auto w-72 shrink-0 invert"
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col items-center flex-1">
                            <JobsiteHours
                                project={projectInfo}
                                iconSize="2x"
                                headerSize="text-xl"
                                secondaryColor="#ffffff"
                            />
                        </div>

                        <div className="flex flex-col items-end ">
                            <Clock timeZone={projectInfo?.timezone} />{" "}
                        </div>
                        <img
                            src="/img/png/logo/plot_jobsite_command.png"
                            alt="PLOT Logo"
                            className="opacity-90 h-28 w-auto shrink-0 invert"
                        />
                    </>
                )}
            </div>
        </header>
    );
};

export default CommandCenterHeader;
